import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const money = (n) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD" });

const moneyCompact = (n) => {
  if (n === 0) return "—";
  if (Math.abs(n) >= 10000) {
    return "$" + (n / 1000).toFixed(n >= 100000 ? 0 : 1) + "k";
  }
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

// Build the 12 fiscal-year months starting at the FY's first month.
const fyMonths = (fromIso) => {
  const start = new Date(fromIso + "T00:00:00");
  const out = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString(undefined, { month: "short" });
    out.push({ ym, label, year: d.getFullYear(), monthIndex: d.getMonth() });
  }
  return out;
};

function Row({ label, dotClass, months, dataByMonth, signInvert = false }) {
  const flip = signInvert ? -1 : 1;
  const filled = months.map((m) => ({
    ...m,
    total: (dataByMonth[m.ym]?.total || 0) * flip,
    count: dataByMonth[m.ym]?.count || 0,
  }));
  const sum = filled.reduce((s, x) => s + x.total, 0);
  const countSum = filled.reduce((s, x) => s + x.count, 0);

  return (
    <div className="mt-block">
      <div className="mt-block-head">
        <p className="mt-block-label">
          <span className={`mt-dot ${dotClass || ""}`} /> {label}
        </p>
        <div className="mt-block-total">
          <span className="muted">
            {countSum} {countSum === 1 ? "txn" : "txns"} ·
          </span>{" "}
          <b>{money(sum)}</b>
        </div>
      </div>
      <div className="mt-grid" data-testid="mt-grid">
        {filled.map((c) => (
          <div
            key={c.ym}
            className={`mt-cell ${c.total === 0 ? "is-zero" : ""}`}
            title={`${c.ym}${c.count ? ` · ${c.count} txn${c.count === 1 ? "" : "s"}` : ""}`}
          >
            <div className="m">{c.label}</div>
            <div className="v" title={money(c.total)}>{moneyCompact(c.total)}</div>
            <div className="c">{c.count > 0 ? `${c.count} txn${c.count === 1 ? "" : "s"}` : ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MonthlyTotals({ calSeq, txn, fyRange, hidePayeeRow = false, signInvert = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const codesId = txn?.codes_id;
  const pyeId   = txn?.pye_id;
  const fromIso = fyRange?.from;

  useEffect(() => {
    if (!calSeq || !codesId) return undefined;
    let ignore = false;
    setLoading(true);
    api.monthlyTotals(calSeq, codesId, pyeId)
      .then((r) => { if (!ignore) setData(r); })
      .catch(() => { if (!ignore) setData(null); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [calSeq, codesId, pyeId]);

  const months = useMemo(
    () => (fromIso ? fyMonths(fromIso) : []),
    [fromIso]
  );

  if (!txn || !fyRange) return null;

  const codeMap = {};
  const codePayeeMap = {};
  if (data) {
    for (const r of data.code_totals) codeMap[r.month] = r;
    for (const r of data.code_payee_totals) codePayeeMap[r.month] = r;
  }

  return (
    <div className="mt-card" data-testid="monthly-card">
      <div className="mt-head">
        <p className="section-title" style={{ margin: 0 }}>Monthly Breakdown</p>
      </div>

      {loading || !data ? (
        <div className="loading-row">
          <span className="spinner" /> Computing monthly totals…
        </div>
      ) : (
        <>
          {!hidePayeeRow && (
            <Row
              label={
                <>
                  CODE <span style={{ color: "var(--accent)" }}>{data.codes_id}</span>
                  {data.codes_lit ? ` · ${data.codes_lit}` : ""}
                  {"  ·  "}
                  {data.pye_nm || data.pye_id} ({data.pye_id})
                </>
              }
              dotClass="purple"
              months={months}
              dataByMonth={codePayeeMap}
              signInvert={signInvert}
            />
          )}
          <Row
            label={
              <>
                CODE <span style={{ color: "var(--accent)" }}>{data.codes_id}</span>
                {data.codes_lit ? ` · ${data.codes_lit}` : ""}
              </>
            }
            dotClass=""
            months={months}
            dataByMonth={codeMap}
            signInvert={signInvert}
          />
        </>
      )}
    </div>
  );
}
